<template>
  <div class="bl-target-details-container">
    <div id="target-details" class="p-6 space-y-4">
      <div class="flex items-center gap-4">
        <BlTextSubtitle1 text="Details" />
        <BlInputButton
          v-if="!readonly"
          type="outlined"
          dense
          label="Edit"
          @clicked="onOpenEditDetails"
        />
      </div>
      <div class="space-y-5">
        <!-- target name -->
        <div class="grid grid-cols-4 gap-x-4">
          <BlTextBody2 class="mt-0.5" text="Title" />
          <BlTextBody1 :text="target.name" class="col-span-3 text-grey" />
        </div>
        <!-- target curriculum -->
        <div class="grid grid-cols-4 gap-x-4">
          <BlTextBody2 class="mt-0.5" text="Curriculum" />
          <div class="flex col-span-3 gap-2">
            <div
              class="flex-shrink-0 rounded-full w-5 h-5 mt-0.5"
              :style="{ backgroundColor: target.curriculum_color }"
            />
            <BlTextBody1 :text="target.curriculum_name" class="text-grey" />
          </div>
        </div>
        <!-- target type -->
        <div class="grid items-center grid-cols-4 gap-x-4">
          <BlTextBody2 text="Data collection method" />
          <BlTextBody1
            :text="targetTypes[target.type]"
            class="col-span-3 text-grey"
          />
        </div>
        <!-- target prompting format -->
        <div
          v-if="target.type.includes('Prompting')"
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Format" />
          <BlTextBody1
            :text="target.prompting_format"
            class="col-span-3 capitalize text-grey"
          />
        </div>
        <!-- target status -->
        <div v-if="!readonly" class="grid items-center grid-cols-4 gap-x-4">
          <BlTextBody2 text="Status" />
          <div class="col-span-3">
            <div class="flex">
              <StatusChip :status="target.status" class="px-3 text-base" />
            </div>
          </div>
        </div>
        <!-- target date introduced -->
        <div v-if="!readonly" class="grid items-center grid-cols-4 gap-x-4">
          <BlTextBody2 text="Date introduced" />
          <BlTextBody1
            :text="displayDate(target.date_introduce)"
            class="col-span-3 text-grey"
          />
        </div>
        <!-- target date mastered -->
        <div v-if="!readonly" class="grid items-center grid-cols-4 gap-x-4">
          <BlTextBody2 text="Date mastered" />
          <BlTextBody1
            :text="displayDate(target.date_mastered)"
            class="col-span-3 text-grey"
          />
        </div>
        <!-- target description -->
        <div class="grid grid-cols-4 gap-x-4">
          <BlTextBody2 class="mt-0.5" text="Description" />
          <BlTextBody1
            :text="target.description || '-'"
            class="col-span-3 text-base whitespace-pre-line text-grey"
          />
        </div>
        <!-- Target::Percentage -->
        <div
          v-if="target.type.includes('Percentage')"
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Goal" />
          <BlTextBody1 :text="`${target.goal}%`" class="col-span-3 text-grey" />
        </div>
        <div
          v-if="target.type.includes('Percentage')"
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Number of trial" />
          <BlTextBody1
            :text="target.number_of_trial"
            class="col-span-3 text-grey"
          />
        </div>
        <!-- Target::Duration -->
        <div
          v-if="target.type.includes('Duration')"
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Goal time" />
          <BlTextBody1 :text="target.goal_time" class="col-span-3 text-grey" />
        </div>
        <!-- Target::Pir -->
        <div
          v-if="target.type.includes('Pir')"
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Goal" />
          <BlTextBody1 :text="`${target.goal}%`" class="col-span-3 text-grey" />
        </div>
        <div
          v-if="target.type.includes('Pir')"
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Interval" />
          <BlTextBody1
            :text="`${target.interval} minutes`"
            class="col-span-3 text-grey"
          />
        </div>
        <div
          v-if="target.type.includes('Pir')"
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Duration" />
          <BlTextBody1
            :text="`${target.duration} minutes`"
            class="col-span-3 text-grey"
          />
        </div>
        <!-- Target::Frequency -->
        <div
          v-if="target.type.includes('Frequency')"
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Goal" />
          <BlTextBody1
            :text="`${target.goal} attempt(s) per session`"
            class="col-span-3 text-grey"
          />
        </div>
        <!-- Target::Prompting -->
        <div
          v-if="
            target.type.includes('Prompting') &&
              target.prompting_format === 'classic'
          "
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Goal and success metric" />
          <div class="col-span-3 text-grey">
            <BlTextBody1 text="Achieve target with" />
            <BlTextBody1 :text="target.success_metric" class="font-bold" />
            <BlTextBody1 text="prompt, minimum" />
            <BlTextBody1
              :text="`${target.goal} attempt(s)`"
              class="font-bold"
            />
            <BlTextBody1 text="per session" />
          </div>
        </div>
        <div
          v-if="
            target.type.includes('Prompting') &&
              target.prompting_format === 'custom'
          "
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Goal" />
          <BlTextBody1 :text="`${target.goal}%`" class="col-span-3 text-grey" />
        </div>
        <!--
          target success metric:
          ✅ percentage, pir, duration, frequency, prompting (custom)
          ❌ sbt, prompting (classic)
        -->
        <div
          v-if="
            !target.type.includes('Sbt') &&
              target.prompting_format !== 'classic'
          "
          class="grid items-center grid-cols-4 gap-x-4"
        >
          <BlTextBody2 text="Success metric" />
          <BlTextBody1
            :text="target.success_metric"
            class="col-span-3 text-grey capitalize-first"
          />
        </div>
      </div>
    </div>
    <EditTargetDetailsModal
      v-if="editDetailsOpened"
      :target="target"
      @close-modal="editDetailsOpened = false"
    />

    <!-- prompting -->
    <div v-if="target.type.includes('Prompting')" id="prompt-selection">
      <div class="w-full h-5 bg-grey-100"></div>
      <div class="grid grid-cols-2 gap-6 p-6">
        <div>
          <div class="flex items-center gap-4 mb-2">
            <BlTextSubtitle1 text="Prompts" />
            <BlInputButton
              v-if="!readonly"
              type="outlined"
              dense
              label="Edit"
              @clicked="onOpenEditPromptSelection"
            />
          </div>
          <div class="mb-4">
            <BlTextBody2
              text="Personalize your card by selecting prompt buttons from the available options. Each symbol represents a specific prompt on the graph."
            />
          </div>
          <div class="flex items-center gap-2 mb-2">
            <BlTextSubtitle3
              v-if="target.prompting_format === 'custom'"
              text="Score (%)"
              class="flex-shrink-0 w-20 text-slate-700"
            />
            <BlTextSubtitle3
              text="Code"
              class="flex-shrink-0 w-10 text-slate-700"
            />
            <BlTextSubtitle3
              text="Symbol"
              class="flex-shrink-0 w-15 text-slate-700"
            />
            <BlTextSubtitle3 text="Prompt name" class="w-full text-slate-700" />
          </div>
          <div
            v-if="target.prompting_format === 'classic'"
            class="flex flex-col gap-2"
          >
            <PromptingClientItem
              v-for="prompt in target.prompts"
              :key="prompt.id"
              :prompt="prompt"
              :format="target.prompting_format"
              readOnly
            />
          </div>
          <div
            v-if="target.prompting_format === 'custom'"
            class="flex flex-col gap-2"
          >
            <div
              v-for="group in groupedCustomPromptingPrompts"
              :key="group.score"
              class="flex gap-2"
            >
              <div
                class="flex items-center justify-center flex-shrink-0 w-20 text-center border rounded bg-prim-100 border-prim-100"
              >
                <BlTextBody1
                  :text="group.score"
                  class="font-semibold text-light-purple"
                />
              </div>

              <div class="flex flex-col gap-2 truncate">
                <PromptingClientItem
                  v-for="prompt in group.prompts"
                  :key="prompt.id"
                  :prompt="prompt"
                  :format="target.prompting_format"
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-center border bg-grey-100 border-grey-200 rounded-3xl"
        >
          <PromptPreview
            :prompts="target.prompts"
            :style="{ transform: 'scale(0.625 , 0.625)' }"
          />
        </div>
      </div>
    </div>
    <EditPromptsModal
      v-if="editPromptingOpened"
      :targetId="target.id"
      :targetName="target.name"
      :successMetric="target.success_metric"
      :format="target.prompting_format"
      :prompts="target.prompts"
      @close-modal="editPromptingOpened = false"
    />
    <!-- prompting -->

    <!-- probing -->
    <div v-if="target.type.includes('Percentage')" id="probing">
      <div class="w-full h-5 bg-grey-100"></div>
      <div class="p-6 space-y-6">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-4">
            <BlTextSubtitle1 text="Probing" />
            <BlInputButton
              v-if="!readonly"
              type="outlined"
              dense
              label="Edit"
              @clicked="onOpenEditProbing"
            />
          </div>
          <BlTextBody2
            text="Probing can be employed to assess a skill at various stages of your therapy. Activating probing gives you additional option to conduct probes during the session."
          />
        </div>
        <div v-if="target.probing_enable" class="space-y-5">
          <div class="grid items-center grid-cols-4 gap-x-4">
            <BlTextBody2 text="Minimum number of trials" />
            <BlTextBody1
              :text="target.probing_number_of_trial"
              class="col-span-3 text-grey"
            />
          </div>
          <div class="grid items-center grid-cols-4 gap-x-4">
            <BlTextBody2 text="Goal for probing" />
            <BlTextBody1
              :text="`≥ ${target.probing_goal}%`"
              class="col-span-3 text-grey"
            />
          </div>
        </div>
        <div v-else>
          <BlTextBody2
            class="italic"
            text="Probing is inactive for this target, and no specific settings have been configured yet."
          />
        </div>
      </div>
    </div>
    <EditProbingModal
      v-if="editProbingOpened"
      :target="target"
      @close-modal="editProbingOpened = false"
    />
    <!-- end probing -->

    <!-- action recommendation -->
    <div v-if="!target.type.includes('Sbt')" id="action-recommendation">
      <div class="w-full h-5 bg-grey-100"></div>
      <div class="p-6 space-y-4">
        <div class="flex items-center gap-2">
          <div class="p-1 bg-orange-300" :style="{ borderRadius: '4px' }">
            <BlIcon icon="tooltip" class="w-5 h-5" />
          </div>
          <BlTextSubtitle1 text="Action recommendations" class="mr-4" />
          <BlInputButton
            v-if="!readonly"
            type="outlined"
            dense
            label="Edit"
            @clicked="onOpenEditActionRecommendation"
          />
        </div>
        <div class="p-6 bg-white border rounded border-grey-200">
          <div class="mb-4 space-y-1">
            <div class="text-grey">
              <BlTextSubtitle2 text="Passing success metric" />
            </div>
            <div class="flex items-center gap-2">
              <BlTextBody2 text="Updating the current target's status from" />
              <StatusChip status="in_progress" dense />
              <BlTextBody2 text="to" />
              <StatusChip status="mastered" dense />
            </div>
          </div>
          <div
            v-if="
              actionRecommendation.total_success === null &&
                actionRecommendation.consecutive_success === null
            "
          >
            <BlTextBody2
              class="italic"
              text="Currently, no action has been set for this recommendation."
            />
          </div>
          <div v-else class="pl-8 space-y-4">
            <div
              v-if="actionRecommendation.total_success !== null"
              class="space-y-1"
            >
              <div class="flex items-center gap-2">
                <BlTextSubtitle2
                  text="Total success"
                  class="text-sm text-grey"
                />
              </div>
              <div class="flex flex-wrap items-center gap-1">
                <BlTextBody2 text="Receive a recommendation after completing" />
                <BlTextBody2
                  class="font-bold"
                  :text="`at least ${actionRecommendation.total_success}`"
                />
                <BlTextBody2 text="successful session(s)." />
              </div>
            </div>
            <div
              v-if="actionRecommendation.consecutive_success !== null"
              class="space-y-1"
            >
              <div class="flex items-center gap-2">
                <BlTextSubtitle2
                  text="Consecutive success"
                  class="text-sm text-grey"
                />
              </div>
              <div class="flex flex-wrap items-center gap-1">
                <BlTextBody2
                  text="Receive a recommendation only if the session is successful for"
                />
                <BlTextBody2
                  class="font-bold"
                  :text="
                    `${actionRecommendation.consecutive_success} consecutive session(s).`
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <EditActionRecommendationsModal
      v-if="editActionRecommendationOpened"
      :actionRecommendation="actionRecommendation"
      @close-modal="editActionRecommendationOpened = false"
    />
    <!-- end action recommendation -->

    <!-- tasks -->
    <div v-if="target.type.includes('Sbt')" id="tasks">
      <div class="w-full h-5 bg-grey-100"></div>
      <div class="p-6 space-y-4">
        <div class="flex items-center gap-4">
          <BlTextSubtitle1 text="Tasks" />
          <BlInputButton
            v-if="!readonly"
            type="outlined"
            dense
            label="Edit task"
            @clicked="editTaskOpened = true"
          />
        </div>
        <div class="px-3 py-2">
          <div
            v-for="(item, i) in target.target_tasks"
            :key="item.id"
            class="flex items-center gap-3"
          >
            <div
              class="flex items-center justify-center flex-shrink-0 w-2 h-2 text-sm font-medium rounded-full bg-slate-600"
            >
              <div class="relative flex flex-col items-center justify-center">
                <div
                  v-if="i !== target.target_tasks.length - 1"
                  class="absolute h-6 transform top-2 bg-slate-600"
                  style="width: 1px;"
                />
              </div>
            </div>

            <div class="flex w-full gap-3 p-2 border-b ">
              <BlTextBody2
                :text="`${item.code} - ${item.title}`"
                class="font-semibold truncate text-slate-1000"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <EditTargetTaskDetailsModal
      v-if="editTaskOpened"
      @close-modal="editTaskOpened = false"
      :target="target"
      :tasks="target.target_tasks"
    />
    <!-- end tasks -->

    <!-- prompt sbt -->
    <div v-if="target.type.includes('Sbt')" id="prompt-sbt">
      <div class="w-full h-5 bg-grey-100"></div>
      <div class="grid grid-cols-2 gap-6 p-6">
        <div>
          <div class="flex items-center gap-4 mb-2">
            <BlTextSubtitle1 text="Prompts" />
            <BlInputButton
              v-if="!readonly"
              type="outlined"
              dense
              label="Edit"
              @clicked="onOpenEditPromptSbt"
            />
          </div>
          <div class="flex items-center gap-2 mt-3">
            <div class="flex-shrink-0 w-20">
              <BlTextSubtitle2 text="Score (%)" class="text-slate-700" />
            </div>
            <div class="w-full ">
              <BlTextSubtitle2 text="Prompt name" class="text-slate-700" />
            </div>
          </div>
          <div class="space-y-16 mt-9">
            <div
              v-for="prompt in groupedSbtPrompts"
              :key="prompt.id"
              class="flex items-center h-6 gap-3"
            >
              <div
                class="flex items-center justify-center w-20 h-20 rounded bg-prim-100"
              >
                <BlTextBody2
                  :text="prompt.score"
                  class="text-lg font-semibold text-light-purple"
                />
              </div>
              <BlTextBody2 :text="prompt.name" class="text-slate-1000" />
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-center border bg-grey-100 border-grey-200 rounded-3xl"
        >
          <SbtPromptingPreview :prompts="sortedPromptsByPosition" />
        </div>
      </div>
    </div>
    <EditPromptsSbtModal
      v-if="editPromptSbtOpened"
      :targetId="target.id"
      :targetName="target.name"
      :prompts="target.prompts"
      @close-modal="editPromptSbtOpened = false"
    />
    <!-- end prompt sbt -->

    <!-- problem behavior -->
    <div v-if="target.type.includes('Sbt')" id="problem-behavior" class="mt-8">
      <div class="w-full h-5 bg-grey-100"></div>
      <div class="p-6 space-y-4">
        <div class="flex items-center gap-4">
          <BlTextSubtitle1 text="Problem behavior" />
          <BlInputButton
            v-if="!readonly"
            type="outlined"
            dense
            label="Edit"
            @clicked="editProblemBehaviorOpened = true"
          />
        </div>
        <div>
          <div class="overflow-x-hidden rounded">
            <div
              class="flex items-center flex-grow-0 w-full top-36 bg-slate-300"
            >
              <div
                class="flex items-center flex-shrink-0 h-12 px-4 rounded-tl"
                :style="{ width: '10%' }"
              >
                <BlTextSubtitle3 text="Code" />
              </div>
              <div
                class="flex items-center flex-shrink-0 h-12 px-4"
                :style="{ width: '25%' }"
              >
                <BlTextSubtitle3 text="Code definition" />
              </div>
              <div
                class="flex items-center flex-shrink-0 h-12 px-4"
                :style="{ width: '50%' }"
              >
                <BlTextSubtitle3 text="Observed behaviors" />
              </div>
              <div
                class="flex items-center flex-shrink-0 h-12 px-4"
                :style="{ width: '50%' }"
              >
                <BlTextSubtitle3 text="Graph  color" />
              </div>
            </div>
            <div
              v-for="item in sortedProblemBehaviorsByPosition"
              :key="item.id"
              class="flex items-center flex-grow-0 w-full border-b border-slate-400 bg-pure-white"
            >
              <div
                class="flex flex-shrink-0 h-20 gap-2 px-4 py-3"
                :style="{ width: '10%' }"
                :title="item.code"
              >
                <BlTextBody1
                  class="line-clamp-2 text-slate-1000 h-13"
                  :text="item.code"
                />
              </div>
              <div
                class="flex-shrink-0 h-20 px-4 py-3"
                :style="{ width: '25%' }"
              >
                <BlTextBody1
                  class="line-clamp-2 h-13"
                  :text="item.code_definition"
                />
              </div>
              <div
                class="flex-shrink-0 h-20 px-4 py-3"
                :style="{ width: '50%' }"
                :title="item.description"
              >
                <BlTextBody1
                  class="whitespace-pre-line line-clamp-2 h-13"
                  :text="item.description || '-'"
                />
              </div>
              <div
                class="flex-shrink-0 h-20 px-4 py-3"
                :style="{ width: '50%' }"
                :title="item.color"
              >
                <div
                  class="w-8 h-8 ml-6"
                  :style="{ backgroundColor: item.color }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <EditTargetBehaviorDetailsModal
      v-if="editProblemBehaviorOpened"
      @close-modal="editProblemBehaviorOpened = false"
      :target="target"
      :targetProblemBehaviors="target.target_problem_behaviors"
    />
    <!-- end problem behavior -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import EditTargetDetailsModal from "./modal/EditTargetDetailsModal.vue";
import PromptPreview from "@/components/repository/prompting/Preview.vue";
import PromptingClientItem from "@/components/repository/prompting/ClientItem.vue";
import SbtPromptingPreview from "@/components/repository/sbt/PromptingPreview.vue";
import EditPromptsModal from "./modal/EditPromptsModal.vue";
import EditProbingModal from "./modal/EditProbingModal.vue";
import EditActionRecommendationsModal from "./modal/EditActionRecommendationsModal.vue";
import EditTargetTaskDetailsModal from "./modal/EditTargetTaskDetailsModal.vue";
import EditTargetBehaviorDetailsModal from "./modal/EditTargetBehaviorDetailsModal.vue";
import EditPromptsSbtModal from "./modal/EditPromptsSbtModal.vue";

import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";
import BlIcon from "../../BlIcon.vue";
import StatusChip from "./StatusChip.vue";

export default {
  name: "TargetPropertyDetails",
  components: {
    EditTargetDetailsModal,
    PromptPreview,
    PromptingClientItem,
    EditPromptsModal,
    EditProbingModal,
    EditActionRecommendationsModal,
    SbtPromptingPreview,
    EditTargetTaskDetailsModal,
    EditPromptsSbtModal,
    EditTargetBehaviorDetailsModal,
    //
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlInputButton,
    BlBackdrop,
    BlIcon,
    StatusChip
  },
  props: ["target", "readonly"],
  data() {
    return {
      editDetailsOpened: false,
      editTaskOpened: false,
      editPromptingOpened: false,
      editProbingOpened: false,
      editActionRecommendationOpened: false,
      editPromptSbtOpened: false,
      editProblemBehaviorOpened: false,
      targetTypes: {
        "Target::Percentage": "Percentage",
        "Target::Pir": "Partial interval recording",
        "Target::Duration": "Duration",
        "Target::Frequency": "Frequency",
        "Target::Prompting": "Prompting",
        "Target::Sbt": "Skill-Based Treatment (SBT)"
      }
    };
  },
  computed: {
    ...mapGetters(["client"]),
    actionRecommendation() {
      const empty = {
        id: null,
        target_id: this.target.id,
        total_success: null,
        consecutive_success: null
      };
      if (!this.target.action_recommendations) return empty;
      if (!this.target.action_recommendations.length) return empty;
      return this.target.action_recommendations.at(0);
    },
    groupedCustomPromptingPrompts() {
      const prompts = this.target.prompts;
      if (!prompts.length) return [];

      const sortedPrompts = prompts
        .sort((a, b) => a.position - b.position)
        .sort((a, b) => {
          if (b.score === a.score) {
            return a.is_default ? 1 : -1;
          }
          return b.score - a.score;
        });

      const grouped = sortedPrompts.reduce((acc, prompt) => {
        const score = prompt.score;
        if (!acc[score]) {
          acc[score] = [];
        }
        acc[score].push(prompt);
        return acc;
      }, {});

      const groupedArray = Object.keys(grouped).map(score => {
        return {
          score: parseInt(score),
          prompts: grouped[score].sort((a, b) => a.position - b.position)
        };
      });

      return groupedArray.sort((a, b) => b.score - a.score);
    },
    groupedSbtPrompts() {
      const grouped = this.target.prompts.reduce((acc, prompt) => {
        if (!acc[prompt.score]) {
          acc[prompt.score] = { score: prompt.score, names: [] };
        }
        acc[prompt.score].names.push(prompt.name);
        return acc;
      }, {});

      const groupedArray = Object.values(grouped);

      groupedArray.sort((a, b) => b.score - a.score);

      return groupedArray.map(group => ({
        score: group.score,
        name: group.names.join(", ")
      }));
    },
    sortedPromptsByPosition() {
      return [...this.target.prompts].sort((a, b) => a.position - b.position);
    },
    sortedProblemBehaviorsByPosition() {
      return [...this.target.target_problem_behaviors].sort(
        (a, b) => a.position - b.position
      );
    }
  },
  methods: {
    displayDate(date) {
      if (!date) return "-";
      return date
        .split("-")
        .reverse()
        .join("/");
    },
    onOpenEditDetails() {
      this.editDetailsOpened = true;
    },
    onOpenEditPromptSelection() {
      this.editPromptingOpened = true;
    },
    onOpenEditProbing() {
      this.editProbingOpened = true;
    },
    onOpenEditActionRecommendation() {
      this.editActionRecommendationOpened = true;
    },
    onOpenEditTask() {
      this.editTaskOpened = true;
    },
    onOpenEditPromptSbt() {
      this.editPromptSbtOpened = true;
    }
  }
};
</script>

<style scoped lang="scss">
.bl-target-details-container {
  position: relative;
  width: 100%;
  // height: calc(100% - 3.5625rem);
  box-sizing: border-box;
  overflow-y: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/clients/target/PropertyDetails.vue